import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React, { useEffect, useRef } from "react";
import { api } from "./utils/api";
import SafeNumberInput from "./SafeNumberInput";
import { addFloatingPanel } from "./utils/DockerviewSettings";

interface OptionData {
  raw_symbol: string;
  expiration: string;
  strike_price: number;
  instrument_class: string;
}

interface Filters {
  expirationPossible: string[];
  expirationSelected: string;
  instrumentSelected: string;
  strike: number;
  maxVariation: number;
}

interface Definitions {
  ticker: string;
  startDate: string;
  daysAgo: number;
  daysIn: number;
}

interface MultiIVOHLCVRequest { 
  ticker: string; 
  contracts: string[];
  startDate: string;
  endDate: string;
  interval: string;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function weekdayFromDate(dateStr: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  return DAYS[d.getDay()];
}

function OptionsDefinitions(props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
}) {
  // State
  const [serverData, setServerData] = React.useState<OptionData[]>([]);
  const [filteredData, setFilteredData] = React.useState<OptionData[]>([]);
  const [filters, setFilters] = React.useState<Filters>({
    expirationPossible: ["All"],
    expirationSelected: "All",
    instrumentSelected: "Calls & Puts",
    strike: 0,
    maxVariation: 0,
  });
  const [definitions, setDefinitions] = React.useState<Definitions>({
    ticker: "",
    startDate: "",
    daysAgo: 5,
    daysIn: 5,
  });
  const [loading, setLoading] = React.useState<boolean>(false);

  useEffect(() => {
    const savedDefs = localStorage.getItem("optDefDefinitions");
    if (savedDefs) {
      try { setDefinitions(JSON.parse(savedDefs)); } catch {}
    }
    const savedFilters = localStorage.getItem("optDefFilters");
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters((prev) => ({ ...prev, ...parsed }));
      } catch {}
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("optDefDefinitions", JSON.stringify(definitions));
  }, [definitions]);

  useEffect(() => {
    localStorage.setItem("optDefFilters", JSON.stringify({
      expirationSelected: filters.expirationSelected,
      instrumentSelected: filters.instrumentSelected,
      strike: filters.strike,
      maxVariation: filters.maxVariation,
    }));
  }, [filters]);

  // Simulating fetching data from a server
  const fetchData = async () => {
    setLoading(true);

    props.api.setTitle("Opt. Def. " + definitions.ticker.toUpperCase());

    // Reset the filters
    filters.expirationPossible = ["All"];
    filters.expirationSelected = "All";
    filters.instrumentSelected = "Calls & Puts";
    filters.strike = 0;
    filters.maxVariation = 0;
    setFilters({ ...filters });

    let url = `/option-definitions?start_date=${definitions.startDate}&ticker=${definitions.ticker}`;

    // get request
    let data;
    try {
      const response = await api.get(url);
      data = response.data;
    } catch (error) {
      console.error("Failed to fetch data:", error);
      return;
    }

    // sort by expiration, then by strike
    data.sort((a: OptionData, b: OptionData) => {
      if (a.expiration < b.expiration) return -1;
      if (a.expiration > b.expiration) return 1;
      if (a.strike_price < b.strike_price) return -1;
      if (a.strike_price > b.strike_price) return 1;

      if (a.instrument_class < b.instrument_class) return -1;
      if (a.instrument_class > b.instrument_class) return 1;
    });

    setServerData(data);

    setFilteredData(data);

    for (let i = 0; i < data.length; i++) {
      let expiration = data[i].expiration;
      if (!filters.expirationPossible.includes(expiration)) {
        filters.expirationPossible.push(expiration);
      }
    }

    setLoading(false);
  };

  function filterData() {
    const newFilteredData = serverData.filter((item) => {
      // filter by date when date is selected
      if (
        filters.expirationSelected !== "All" &&
        item.expiration !== filters.expirationSelected
      ) {
        return false;
      }

      // filter by calls vs puts
      if (
        filters.instrumentSelected !== "Calls & Puts" &&
        item.instrument_class !== filters.instrumentSelected.substring(0, 1)
      ) {
        return false;
      }

      // filter by strike price
      if (filters.strike !== 0) {
        const strikePrice = item.strike_price;
        const variation = filters.maxVariation || 0;

        const inPositiveVariation =
          variation >= 0 &&
          strikePrice <= filters.strike + variation &&
          strikePrice >= filters.strike;

        const inNegativeVariation =
          variation < 0 &&
          strikePrice >= filters.strike + variation &&
          strikePrice <= filters.strike;

        if (!inPositiveVariation && !inNegativeVariation) {
          return false;
        }
      }

      return true;
    });

    setFilteredData(newFilteredData);
  }

  function sortData(key: keyof OptionData) {
    const sortedData = [...filteredData].sort((a, b) => {
      if (a[key] < b[key]) return -1;
      if (a[key] > b[key]) return 1;
      return 0;
    });

    setFilteredData(sortedData);
  }

  return (
    <div className="w-full h-full overflow-auto">
      <div className="m-4 w-[600px]">
        <OptionsSearch
          definitions={definitions}
          setDefinitions={setDefinitions}
          fetchData={fetchData}
          containerApi={props.containerApi}
        />

        <OptionsFilter
          filters={filters}
          setFilters={setFilters}
          filterData={filterData}
        />

        <TableComponent
          key={JSON.stringify(filteredData)}
          data={filteredData}
          loading={loading}
          sortData={sortData}
          containerApi={props.containerApi}
          ticker={definitions.ticker}
        />
      </div>
    </div>
  );
}

export default OptionsDefinitions;

const OptionsSearch = ({
  definitions,
  setDefinitions,
  fetchData,
  containerApi,
}: {
  definitions: Definitions;
  setDefinitions: React.Dispatch<React.SetStateAction<Definitions>>;
  fetchData: () => void;
  containerApi: DockviewApi;
}) => {
  const autoFocusRef = useRef(null);

  useEffect(() => {
    if (autoFocusRef.current) {
      (autoFocusRef.current as HTMLInputElement).focus();
    }
  }, []);

  function addDays(dateStr: string, days: number): string {
    const d = new Date(dateStr + "T00:00:00");
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }

  return (
    <section>
      <form
        onSubmit={(e) => {
          e.currentTarget.blur();
          e.preventDefault();
          fetchData();

          if (definitions.startDate && definitions.ticker) {
            const startDate = addDays(definitions.startDate, -definitions.daysAgo);
            const endDate = addDays(definitions.startDate, definitions.daysIn);
            addFloatingPanel(containerApi, "equity_chart", {
              ticker: definitions.ticker,
              startDate,
              endDate,
              interval: "h",
            });
          }
        }}
      >
        <fieldset className="p-4">
          <legend>Active options def.</legend>
          <div className="form-group">
            <label htmlFor="ticker">Ticker:</label>
            <input
              ref={autoFocusRef}
              id="ticker"
              type="text"
              name="userInput"
              className="w-full"
              required
              value={definitions.ticker}
              onChange={(e) =>
                setDefinitions({
                  ...definitions,
                  ticker: e.target.value,
                })
              }
            />
          </div>

          <div className="flex gap-4 items-center">
            <div className="form-group">
              <label htmlFor="start-date">Active as of:{weekdayFromDate(definitions.startDate) && ` (${weekdayFromDate(definitions.startDate)})`}</label>
              <input
                type="text"
                id="start-date"
                name="dateInput"
                className="block border-black border"
                required
                value={definitions.startDate}
                onChange={(e) =>
                  setDefinitions({
                    ...definitions,
                    startDate: e.target.value,
                  })
                }
                placeholder="YYYY-MM-DD"
              />
            </div>

            <div className="flex">until at least next day</div>
          </div>

          <div className="flex gap-4 items-center">
            <div className="form-group flex-1">
              <label htmlFor="days-ago">Days ago: {definitions.daysAgo}</label>
              <input
                type="range"
                id="days-ago"
                min={1}
                max={60}
                value={definitions.daysAgo}
                onChange={(e) =>
                  setDefinitions({
                    ...definitions,
                    daysAgo: Number(e.target.value),
                  })
                }
                className="w-full"
              />
            </div>

            <div className="form-group flex-1">
              <label htmlFor="days-in">Days in: {definitions.daysIn}</label>
              <input
                type="range"
                id="days-in"
                min={1}
                max={60}
                value={definitions.daysIn}
                onChange={(e) =>
                  setDefinitions({
                    ...definitions,
                    daysIn: Number(e.target.value),
                  })
                }
                className="w-full"
              />
            </div>
          </div>

          <div className="form-group">
            <button
              className="btn btn-default"
              type="submit"
              name="submit"
              id="submit"
            >
              Submit
            </button>
          </div>
        </fieldset>
      </form>
    </section>
  );
};

const OptionsFilter = ({
  filters,
  setFilters,
  filterData,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  filterData: () => void;
}) => {
  return (
    <section>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          filterData();
        }}
      >
        <fieldset className="p-4">
          <legend>Filter</legend>
          <div className="form-group flex gap-4">
            <div>
              <label htmlFor="select">Expiration:</label>
              <select
                id="select"
                name="select"
                className="block border-black border"
                value={filters.expirationSelected}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    expirationSelected: e.target.value,
                  })
                }
              >
                {filters.expirationPossible.map((date: string, index) => (
                  <option key={index}>{date}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="select">Instrument:</label>
              <select
                id="select"
                name="select"
                className="block border-black border"
                value={filters.instrumentSelected}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    instrumentSelected: e.target.value,
                  })
                }
              >
                <option>Calls & Puts</option>
                <option>Calls</option>
                <option>Puts</option>
              </select>
            </div>
          </div>

          <div className="form-group flex gap-4 items-baseline">
            <div>
              <label htmlFor="strike">Strike:</label>
              <SafeNumberInput
                id="strike"
                value={filters.strike}
                min={0}
                step={1}
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    strike: value,
                  });
                }}
                placeholder="No restriction"
              />
            </div>

            <div>
              <label htmlFor="strike-variantion">Max variation:</label>

              <SafeNumberInput
                id="strike-variantion"
                min={-1000}
                max={1000}
                step={1}
                value={filters.maxVariation}
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    maxVariation: value,
                  });
                }}
                placeholder="No restriction"
              />

              {/* <input
                id="strike-variantion"
                type="number"
                placeholder="No restriction"
                value={filters.maxVariation ? filters.maxVariation : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilters({
                    ...filters,
                    maxVariation: v === "" ? null : Number(v),
                  });
                }}
              /> */}
            </div>
          </div>

          <div className="form-group">
            <button
              className="btn btn-default"
              type="submit"
              name="submit"
              id="submit"
            >
              Filter
            </button>
          </div>
        </fieldset>
      </form>
    </section>
  );
};

interface IMultiIVRequest {
  startDate: string;
  endDate: string;
  contracts: string[];
  showUnderlying: boolean;
}

interface IOHLCVRequest {
  startDate: string;
  endDate: string;
  interval: string;
}

const TableComponent = ({
  data,
  loading,
  sortData,
  containerApi,
  ticker,
}: {
  data: OptionData[];
  loading: boolean;
  sortData: (key: keyof OptionData) => void;
  containerApi: DockviewApi;
  ticker: string;
}) => {
  const [dataRequest, setDataRequest] = React.useState<IMultiIVRequest>({
    startDate: "",
    endDate: "",
    contracts: [],
    showUnderlying: false,
  });

  const [ohlcvRequest, setOhlcvRequest] = React.useState<IOHLCVRequest>({
    startDate: "",
    endDate: "",
    interval: "h",
  });

  useEffect(() => {
    // get from local storage
    const saved = localStorage.getItem("multi_iv_data_request");

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setDataRequest({
          startDate: parsed.startDate || "",
          endDate: parsed.endDate || "",
          contracts: [],
          showUnderlying: parsed.showUnderlying,
        });
      } catch (error) {
        console.error("Failed to parse saved data request:", error);
      }
    }

    const savedOhlcv = localStorage.getItem("ohlcv_data_request");
    if (savedOhlcv) {
      try {
        const parsed = JSON.parse(savedOhlcv);
        setOhlcvRequest({
          startDate: parsed.startDate || "",
          endDate: parsed.endDate || "",
          interval: parsed.interval || "h",
        });
      } catch (error) {
        console.error("Failed to parse saved OHLCV request:", error);
      }
    }
  }, []);

  if (loading) {
    return <div className="w-full text-center">Loading...</div>;
  }

  if (data.length === 0) {
    return <div className="w-full text-center">No data available</div>;
  }

  return (
    <div>
      <table>
        <caption>Results</caption>
        <thead>
          <tr>
            <th>Select</th>
            <th
              className="cursor-pointer"
              onClick={() => sortData("raw_symbol")}
            >
              Raw Symbol
            </th>
            <th
              className="cursor-pointer"
              onClick={() => sortData("expiration")}
            >
              Expiration
            </th>
            <th
              className="cursor-pointer"
              onClick={() => sortData("strike_price")}
            >
              Strike
            </th>
            <th
              className="cursor-pointer"
              onClick={() => sortData("instrument_class")}
            >
              Class
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={item.raw_symbol}>
              <td>
                <div
                  className={`w-14 h-5 ${
                    dataRequest.contracts.includes(item.raw_symbol)
                      ? "bg-blue-600"
                      : "bg-gray-200"
                  } cursor-pointer`}
                  onClick={() => {
                    if (dataRequest.contracts.includes(item.raw_symbol)) {
                      setDataRequest({
                        ...dataRequest,
                        contracts: dataRequest.contracts.filter(
                          (c) => c !== item.raw_symbol
                        ),
                      });
                    } else {
                      setDataRequest({
                        ...dataRequest,
                        contracts: [...dataRequest.contracts, item.raw_symbol],
                      });
                    }
                  }}
                ></div>
              </td>

              <td>
                <p className="whitespace-pre-wrap font-mono break-words">
                  {item.raw_symbol}
                </p>
              </td>
              <td>{item.expiration}</td>
              <td>{item.strike_price}</td>
              <td>{item.instrument_class}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <form
        className="flex gap-4"
        onSubmit={(e) => {
          e.preventDefault();

          // save to local storage
          localStorage.setItem(
            "multi_iv_data_request",
            JSON.stringify(dataRequest)
          );

          addFloatingPanel(containerApi, "multi_iv", dataRequest);
        }}
      >
        <label htmlFor="startDate">
          Start Date:
          <input
            placeholder="YYYY-MM-DD HH:MM"
            type="text"
            id="startDate"
            name="startDate"
            value={dataRequest.startDate}
            onChange={(e) => {
              setDataRequest({
                ...dataRequest,
                startDate: e.target.value,
              });
            }}
          />
        </label>

        <label htmlFor="endDate">
          End Date:
          <input
            placeholder="YYYY-MM-DD HH:MM"
            type="text"
            id="endDate"
            name="endDate"
            value={dataRequest.endDate}
            onChange={(e) => {
              setDataRequest({
                ...dataRequest,
                endDate: e.target.value,
              });
            }}
          />
        </label>

        {/* True / False selector */}
        <label htmlFor="showUnderlying">
          <input
          className="mr-2"
            type="checkbox"
            id="showUnderlying"
            name="showUnderlying"
            checked={dataRequest.showUnderlying}
            onChange={(e) => {
              setDataRequest({
                ...dataRequest,
                showUnderlying: e.target.checked,
              });
            }}
          />
          Show Und
        </label>


        <button
          type="submit"
          className="btn btn-default whitespace-nowrap h-12 mt-5"
        >
          &gt; Multi IV
        </button>
      </form>

      <form
        className="w-full flex justify-end gap-4 mt-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ohlcvRequest.startDate || !ohlcvRequest.endDate) {
            alert("Please fill in Start Date and End Date before opening OHLCV.");
            return;
          }
          if (dataRequest.contracts.length === 0) {
            alert("Please select at least one contract before opening OHLCV.");
            return;
          }
          localStorage.setItem("ohlcv_data_request", JSON.stringify(ohlcvRequest));
          addFloatingPanel(containerApi, "iv_ohlcv", {
            ticker,
            contracts: dataRequest.contracts,
            start_str: ohlcvRequest.startDate,
            end_str: ohlcvRequest.endDate,
            interval: ohlcvRequest.interval,
          });
        }}
      >
        <label htmlFor="ohlcvStartDate">
          Start Date:
          <input
            placeholder="YYYY-MM-DD"
            type="text"
            id="ohlcvStartDate"
            name="startDate"
            value={ohlcvRequest.startDate}
            onChange={(e) => setOhlcvRequest({ ...ohlcvRequest, startDate: e.target.value })}
          />
        </label>

        <label htmlFor="ohlcvEndDate">
          End Date:
          <input
            placeholder="YYYY-MM-DD"
            type="text"
            id="ohlcvEndDate"
            name="endDate"
            value={ohlcvRequest.endDate}
            onChange={(e) => setOhlcvRequest({ ...ohlcvRequest, endDate: e.target.value })}
          />
        </label>

        <label htmlFor="ohlcvInterval" className="w-32">
          Interval:
          <input
            placeholder="s / m / h / d"
            type="text"
            id="ohlcvInterval"
            name="interval"
            value={ohlcvRequest.interval}
            onChange={(e) => setOhlcvRequest({ ...ohlcvRequest, interval: e.target.value })}
          />
        </label>

        <button
          type="submit"
          className="btn btn-default whitespace-nowrap h-12 mt-5"
        >
          &gt; OHLCV
        </button>
      </form>


      <h1>Selected:</h1>
      {dataRequest.contracts.length === 0 ? (
        <div>None</div>
      ) : (
        <div>{dataRequest.contracts.join(", ")}</div>
      )}
    </div>
  );
};
